/**
 * Inline UOM Autocomplete Component
 * Rich combobox for Unit of Measure selection with search and custom entry.
 * Uses React Portal to render dropdown outside overflow containers.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Check, Plus, Package, Ruler, Scale, Droplets } from 'lucide-react';

// Standard UOM options with categories and descriptions
const UOM_OPTIONS = [
  // Quantity Units
  { value: 'EA', label: 'Each', description: 'Individual Unit', category: 'quantity', icon: Package },
  { value: 'PC', label: 'Piece', description: 'Single Piece', category: 'quantity', icon: Package },
  { value: 'SET', label: 'Set', description: 'Multiple Items Together', category: 'quantity', icon: Package },
  { value: 'KIT', label: 'Kit', description: 'Assembly of Parts', category: 'quantity', icon: Package },
  { value: 'BOX', label: 'Box', description: 'Boxed Package', category: 'quantity', icon: Package },
  { value: 'PKG', label: 'Package', description: 'Packaged Unit', category: 'quantity', icon: Package },
  { value: 'PR', label: 'Pair', description: 'Two Items', category: 'quantity', icon: Package },
  { value: 'DZ', label: 'Dozen', description: '12 Units', category: 'quantity', icon: Package },
  
  // Length Units
  { value: 'FT', label: 'Feet', description: 'Imperial Length', category: 'length', icon: Ruler },
  { value: 'IN', label: 'Inch', description: 'Imperial Length', category: 'length', icon: Ruler },
  { value: 'M', label: 'Meter', description: 'Metric Length', category: 'length', icon: Ruler },
  { value: 'CM', label: 'Centimeter', description: 'Metric Length', category: 'length', icon: Ruler },
  { value: 'MM', label: 'Millimeter', description: 'Metric Length', category: 'length', icon: Ruler },
  { value: 'YD', label: 'Yard', description: 'Imperial Length', category: 'length', icon: Ruler },
  
  // Weight Units
  { value: 'LB', label: 'Pound', description: 'Imperial Weight', category: 'weight', icon: Scale },
  { value: 'OZ', label: 'Ounce', description: 'Imperial Weight', category: 'weight', icon: Scale },
  { value: 'KG', label: 'Kilogram', description: 'Metric Weight', category: 'weight', icon: Scale },
  { value: 'G', label: 'Gram', description: 'Metric Weight', category: 'weight', icon: Scale },
  
  // Volume Units
  { value: 'L', label: 'Liter', description: 'Metric Volume', category: 'volume', icon: Droplets },
  { value: 'ML', label: 'Milliliter', description: 'Metric Volume', category: 'volume', icon: Droplets },
  { value: 'GAL', label: 'Gallon', description: 'Imperial Volume', category: 'volume', icon: Droplets },
  { value: 'QT', label: 'Quart', description: 'Imperial Volume', category: 'volume', icon: Droplets },
  { value: 'PT', label: 'Pint', description: 'Imperial Volume', category: 'volume', icon: Droplets },
];

const InlineUOMAutocomplete = ({
  value,
  onChange,
  onFocus,
  onKeyDown,
  cellRef,
  className = "",
  placeholder = "EA",
  testId = "uom-cell"
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [filteredOptions, setFilteredOptions] = useState(UOM_OPTIONS);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const isSelectingRef = useRef(false);
  const isUserClickRef = useRef(false);
  const isEditingRef = useRef(false); // Track if user is actively editing

  // Sync external value changes (only when not editing)
  useEffect(() => {
    if (!isEditingRef.current) {
      setInputValue(value ?? '');
    }
  }, [value]);

  // Expose input ref to parent
  useEffect(() => {
    if (cellRef) {
      cellRef(inputRef.current);
    }
  }, [cellRef]);

  // Filter options based on input
  useEffect(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) {
      setFilteredOptions(UOM_OPTIONS);
    } else {
      const filtered = UOM_OPTIONS.filter(opt => 
        opt.value.toLowerCase().includes(query) ||
        opt.label.toLowerCase().includes(query) ||
        opt.description.toLowerCase().includes(query)
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue]);

  // Calculate dropdown position when opening
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: Math.max(rect.width, 260)
      });
    }
  }, []);

  // Update position on scroll/resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value.toUpperCase(); // UOM typically uppercase
    setInputValue(newValue);
    setHighlightedIndex(-1);
    if (!isOpen) {
      setIsOpen(true);
    }
    onChange(newValue);
  };

  // Handle option selection
  const handleSelect = (option) => {
    isSelectingRef.current = true;
    const val = typeof option === 'string' ? option : option.value;
    setInputValue(val);
    onChange(val);
    setIsOpen(false);
    setHighlightedIndex(-1);
    
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  // Handle "Create custom" option
  const handleCreateCustom = () => {
    const trimmedValue = inputValue.trim().toUpperCase();
    if (trimmedValue) {
      isSelectingRef.current = true;
      onChange(trimmedValue);
      setIsOpen(false);
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    }
  };

  // Handle focus - only open on user click, and select all text for easy replacement
  const handleFocus = (e) => {
    isEditingRef.current = true; // Start editing mode
    updateDropdownPosition();
    
    if (isUserClickRef.current) {
      setIsOpen(true);
    }
    
    isUserClickRef.current = false;
    
    if (onFocus) {
      onFocus(e);
    }
  };

  // Handle click - select all text for easy replacement
  const handleClick = (e) => {
    // Select all text when clicking on the input
    if (inputRef.current && inputRef.current.value) {
      inputRef.current.select();
    }
  };

  // Track user clicks
  const handleMouseDown = () => {
    isUserClickRef.current = true;
  };

  // Handle blur
  const handleBlur = () => {
    setTimeout(() => {
      if (isSelectingRef.current) {
        return;
      }
      isEditingRef.current = false; // End editing mode
      setIsOpen(false);
      // Commit current value on blur (even if empty - let parent handle defaults)
      const finalValue = inputValue.trim().toUpperCase();
      onChange(finalValue);
    }, 150);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (isOpen) {
      const maxIndex = filteredOptions.length + (showCreateOption ? 0 : -1);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex(prev => Math.min(prev + 1, maxIndex));
          return;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex(prev => Math.max(prev - 1, -1));
          return;
        case 'Enter':
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(filteredOptions[highlightedIndex]);
            return;
          } else if (highlightedIndex === filteredOptions.length && showCreateOption) {
            e.preventDefault();
            e.stopPropagation();
            handleCreateCustom();
            return;
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          setHighlightedIndex(-1);
          return;
        case 'Tab':
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            handleSelect(filteredOptions[highlightedIndex]);
          }
          setIsOpen(false);
          break;
        default:
          break;
      }
    } else {
      if (e.key === 'ArrowDown' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        updateDropdownPosition();
        setIsOpen(true);
        return;
      }
    }
    
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedContainer = containerRef.current?.contains(event.target);
      const clickedDropdown = dropdownRef.current?.contains(event.target);
      
      if (!clickedContainer && !clickedDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if we should show "Create custom" option
  const hasExactMatch = UOM_OPTIONS.some(
    opt => opt.value.toLowerCase() === inputValue.trim().toLowerCase()
  );
  const showCreateOption = inputValue.trim() && !hasExactMatch;

  // Get category color
  const getCategoryStyle = (category) => {
    switch (category) {
      case 'quantity': return 'bg-blue-500/20 text-blue-300';
      case 'length': return 'bg-green-500/20 text-green-300';
      case 'weight': return 'bg-orange-500/20 text-orange-300';
      case 'volume': return 'bg-purple-500/20 text-purple-300';
      default: return 'bg-slate-500/20 text-slate-300';
    }
  };

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;
    
    const queryLower = query.toLowerCase();
    const textLower = text.toLowerCase();
    const index = textLower.indexOf(queryLower);
    
    if (index === -1) return text;
    
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 text-yellow-900 font-semibold">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  };

  // Find current option for display
  const currentOption = UOM_OPTIONS.find(opt => opt.value === inputValue);

  // Dropdown content rendered via Portal
  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      role="listbox"
      className="fixed bg-slate-800 border border-slate-600 rounded-lg shadow-2xl overflow-hidden"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999
      }}
      data-testid="inline-uom-dropdown"
    >
      {/* Header */}
      <div className="px-2.5 py-1.5 bg-slate-700/50 border-b border-slate-600">
        <span className="text-xs font-medium text-slate-400">
          {inputValue.trim() ? 'Matching Units' : 'Select Unit of Measure'}
        </span>
      </div>

      {/* Options List */}
      <div className="max-h-56 overflow-y-auto">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            const Icon = option.icon;
            const isSelected = option.value === inputValue;
            
            return (
              <button
                key={option.value}
                role="option"
                aria-selected={highlightedIndex === index}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(option);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`w-full px-2.5 py-2 flex items-center gap-2.5 text-left transition-colors ${
                  highlightedIndex === index
                    ? 'bg-blue-600/30'
                    : 'hover:bg-slate-700'
                } ${isSelected ? 'bg-emerald-600/20' : ''}`}
                data-testid={`uom-option-${option.value}`}
              >
                {/* Icon */}
                <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${getCategoryStyle(option.category)}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${isSelected ? 'text-emerald-300' : 'text-white'}`}>
                      {highlightMatch(option.value, inputValue)}
                    </span>
                    <span className="text-sm text-slate-400">
                      {highlightMatch(option.label, inputValue)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {option.description}
                  </p>
                </div>

                {/* Selected indicator */}
                {isSelected && (
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                )}
              </button>
            );
          })
        ) : (
          <div className="px-3 py-3 text-center text-slate-500 text-sm">
            No matching units found
          </div>
        )}

        {/* Create Custom Option */}
        {showCreateOption && (
          <button
            role="option"
            aria-selected={highlightedIndex === filteredOptions.length}
            onMouseDown={(e) => {
              e.preventDefault();
              handleCreateCustom();
            }}
            onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
            className={`w-full px-2.5 py-2.5 flex items-center gap-2.5 text-left border-t border-slate-600 transition-colors ${
              highlightedIndex === filteredOptions.length
                ? 'bg-emerald-600/30'
                : 'hover:bg-slate-700'
            }`}
            data-testid="uom-create-custom"
          >
            <div className="w-7 h-7 rounded-full bg-emerald-600/30 flex items-center justify-center flex-shrink-0">
              <Plus className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <span className="text-sm text-emerald-400 font-medium">
                Use "{inputValue.trim().toUpperCase()}" as custom unit
              </span>
              <p className="text-xs text-slate-500">
                Add a custom unit of measure
              </p>
            </div>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="inline-uom-listbox"
        aria-activedescendant={highlightedIndex >= 0 ? `uom-option-${highlightedIndex}` : undefined}
      />

      {/* Render dropdown via Portal */}
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default InlineUOMAutocomplete;
